import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Form,
  Modal,
  Spinner,
  Table,
} from 'react-bootstrap';
import { getFieldValueLabel } from './configurationAdminUtils';
import {
  analyzeValueRuleBulkRows,
  createBlankValueRuleBulkRow,
  createValueRuleBulkRows,
  getNextBulkValueRuleGroupId,
  getValueRuleOptionLabel,
  getValueRuleTemplateRows,
  mergeImportedValueRuleRows,
  parseValueRuleImportMatrix,
} from './valueRuleBulkUtils';

const loadSpreadsheetLibrary = () => import('xlsx');

const safeFilenamePart = (value) => String(value || 'rules')
  .trim()
  .replace(/[^a-z0-9]+/gi, '-')
  .replace(/^-|-$/g, '')
  .toLowerCase();

const buildWorkbook = (XLSX, {
  station,
  field,
  rules,
  fieldValues,
  referenceFields,
  referenceValues,
}) => {
  const workbook = XLSX.utils.book_new();
  const instructions = XLSX.utils.aoa_to_sheet([
    ['Value Rules bulk upload'],
    ['Selected station', station.stationName],
    ['ParentStationId', station.id],
    ['Selected field', field.fieldName],
    ['ConfigFieldId', field.id],
    [],
    ['Instructions'],
    ['Each spreadsheet row becomes one SP_ConfigValueRules API row.'],
    ['Leave Id blank to create a row. Use an existing Id only to update that exact row.'],
    ['Each RuleGroupId + FieldValueId branch must contain exactly one Station row.'],
    ['Station rows use ParentType Station and ParentStationId; ParentFieldValueId must be blank.'],
    ['AND dependencies use ParentType FieldValue and ParentFieldValueId; ParentStationId must be blank.'],
    ['Rows in the same branch must use the same IsDisabled and IsDefaultValue settings.'],
    ['Different RuleGroupId values are OR groups. Omitted rows are unchanged; bulk mode never deletes rules.'],
    ['Use the Lookups sheet to find valid FieldValueId and ParentFieldValueId values.'],
  ]);
  instructions['!cols'] = [{ wch: 26 }, { wch: 88 }];
  const rulesSheet = XLSX.utils.aoa_to_sheet(getValueRuleTemplateRows(rules));
  rulesSheet['!cols'] = [
    { wch: 12 },
    { wch: 16 },
    { wch: 16 },
    { wch: 16 },
    { wch: 20 },
    { wch: 24 },
    { wch: 14 },
    { wch: 18 },
  ];
  const fieldById = new Map(referenceFields.map((candidate) => [candidate.id, candidate]));
  const lookupRows = [
    ['Use As', 'Field', 'ConfigFieldId', 'Value', 'FieldValueId'],
    ...fieldValues.map((value) => ['Allowed Value', field.fieldName, field.id, getFieldValueLabel(value), value.id]),
    ...referenceValues
      .filter((value) => value.configFieldId !== field.id)
      .map((value) => {
        const dependencyField = fieldById.get(value.configFieldId);
        return ['Dependency', dependencyField?.fieldName || `Field ${value.configFieldId}`, value.configFieldId, getFieldValueLabel(value), value.id];
      })
      .sort((left, right) => left[1].localeCompare(right[1]) || String(left[3]).localeCompare(String(right[3]), undefined, { numeric: true })),
  ];
  const lookupsSheet = XLSX.utils.aoa_to_sheet(lookupRows);
  lookupsSheet['!cols'] = [{ wch: 18 }, { wch: 34 }, { wch: 18 }, { wch: 48 }, { wch: 18 }];
  XLSX.utils.book_append_sheet(workbook, instructions, 'Instructions');
  XLSX.utils.book_append_sheet(workbook, rulesSheet, 'Value Rules');
  XLSX.utils.book_append_sheet(workbook, lookupsSheet, 'Lookups');
  return workbook;
};

const readWorkbookMatrix = async (file) => {
  const XLSX = await loadSpreadsheetLibrary();
  const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
  const sheet = workbook.Sheets['Value Rules'] || workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet) throw new Error('The workbook does not contain a readable sheet.');
  return XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false });
};

const ValueRuleBulkEditor = ({
  show,
  station,
  field,
  fieldValues,
  baselineRules,
  allRules,
  referenceFields,
  referenceValues,
  saving,
  validation,
  onClose,
  onSave,
}) => {
  const [rows, setRows] = useState([]);
  const [importError, setImportError] = useState('');
  const [importNotice, setImportNotice] = useState('');
  const [pasteText, setPasteText] = useState('');
  const [showPaste, setShowPaste] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!show || !station || !field) return;
    setRows(createValueRuleBulkRows(baselineRules));
    setImportError('');
    setImportNotice('');
    setPasteText('');
    setShowPaste(false);
  }, [baselineRules, field, show, station]);

  const analysis = useMemo(() => (station && field ? analyzeValueRuleBulkRows({
    rows,
    baselineRules,
    allRules,
    stationId: station.id,
    fieldId: field.id,
    fieldValues: referenceValues,
  }) : null), [allRules, baselineRules, field, referenceValues, rows, station]);

  const dependencyGroups = useMemo(() => referenceFields
    .filter((candidate) => candidate.id !== field?.id)
    .map((candidate) => ({
      field: candidate,
      values: referenceValues
        .filter((value) => value.configFieldId === candidate.id)
        .sort((left, right) => getFieldValueLabel(left).localeCompare(getFieldValueLabel(right), undefined, { numeric: true })),
    }))
    .filter((group) => group.values.length > 0)
    .sort((left, right) => left.field.fieldName.localeCompare(right.field.fieldName)), [field, referenceFields, referenceValues]);

  if (!station || !field || !analysis) return null;

  const updateRow = (clientKey, changes) => {
    setRows((current) => {
      const sourceRow = current.find((row) => row.clientKey === clientKey);
      if (!sourceRow) return current;
      const propagatesStatus = Object.prototype.hasOwnProperty.call(changes, 'isDisabled')
        || Object.prototype.hasOwnProperty.call(changes, 'isDefaultValue');
      return current.map((row) => {
        const sameBranch = Number(row.ruleGroupId) === Number(sourceRow.ruleGroupId)
          && Number(row.fieldValueId) === Number(sourceRow.fieldValueId);
        if (row.clientKey !== clientKey && (!propagatesStatus || !sameBranch)) return row;
        const next = { ...row, ...changes };
        if (next.parentType === 'station') {
          next.parentStationId = station.id;
          next.parentFieldValueId = '';
        } else if (next.parentType === 'fieldValue') {
          next.parentStationId = '';
        }
        return next;
      });
    });
  };

  const addRuleGroup = () => {
    setRows((current) => [...current, createBlankValueRuleBulkRow({
      stationId: station.id,
      fieldValueId: fieldValues[0]?.id || '',
      ruleGroupId: getNextBulkValueRuleGroupId(allRules, current),
    })]);
  };

  const addDependency = () => {
    setRows((current) => {
      const source = [...current].reverse().find((row) => Number(row.fieldValueId) > 0 && Number(row.ruleGroupId) > 0);
      if (!source) return current;
      return [...current, createBlankValueRuleBulkRow({
        stationId: station.id,
        fieldValueId: source.fieldValueId,
        ruleGroupId: source.ruleGroupId,
        parentType: 'fieldValue',
        parentFieldValueId: dependencyGroups[0]?.values[0]?.id || '',
        isDisabled: source.isDisabled,
        isDefaultValue: source.isDefaultValue,
      })];
    });
  };

  const importMatrix = (matrix, sourceLabel) => {
    const imported = parseValueRuleImportMatrix(matrix);
    const nextRows = mergeImportedValueRuleRows(rows, imported);
    setRows(nextRows);
    setImportError('');
    setImportNotice(`${imported.length} row${imported.length === 1 ? '' : 's'} imported from ${sourceLabel}. Review validation and changes before saving.`);
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      importMatrix(await readWorkbookMatrix(file), file.name);
    } catch (error) {
      setImportNotice('');
      setImportError(error.message || 'Unable to read the selected file.');
    }
  };

  const handlePasteImport = async () => {
    try {
      const XLSX = await loadSpreadsheetLibrary();
      const workbook = XLSX.read(pasteText, { type: 'string' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      importMatrix(XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false }), 'the pasted table');
      setPasteText('');
    } catch (error) {
      setImportNotice('');
      setImportError(error.message || 'Unable to read the pasted table.');
    }
  };

  const downloadSpreadsheet = async (includeCurrentRules) => {
    try {
      const XLSX = await loadSpreadsheetLibrary();
      const workbook = buildWorkbook(XLSX, {
        station,
        field,
        rules: includeCurrentRules ? baselineRules : [],
        fieldValues,
        referenceFields,
        referenceValues,
      });
      const suffix = includeCurrentRules ? 'current-value-rules' : 'value-rules-upload-template';
      XLSX.writeFile(workbook, `${safeFilenamePart(station.stationName)}-${safeFilenamePart(field.fieldName)}-${suffix}.xlsx`);
    } catch (error) {
      setImportNotice('');
      setImportError(error.message || 'Unable to create the spreadsheet.');
    }
  };

  const hasErrors = analysis.errorCount > 0;
  const dependencyCount = rows.filter((row) => row.parentType === 'fieldValue').length;

  return (
    <Modal show={show} onHide={saving ? undefined : onClose} size="xl" centered dialogClassName="config-admin-bulk-modal config-admin-rule-bulk-modal">
      <Modal.Header closeButton={!saving}><Modal.Title>Bulk edit and upload value rules</Modal.Title></Modal.Header>
      <Modal.Body>
        <div className="config-admin-bulk-scope config-admin-rule-bulk-scope">
          <div><small>Station</small><strong>{station.stationName}</strong><span>ID {station.id}</span></div>
          <div><small>Field</small><strong>{field.fieldName}</strong><span>ID {field.id}</span></div>
          <div><small>Logic</small><strong>{analysis.groupCount} OR groups</strong><span>{analysis.branchCount} allowed value branches · {dependencyCount} AND dependencies</span></div>
        </div>

        <Alert variant="info" className="small">
          Each row is one API condition. A Rule Group and Allowed Value branch requires exactly one Station row; additional Field Value rows are AND dependencies. Different Rule Group IDs are OR. Bulk mode never deletes omitted rules.
        </Alert>
        {validation && <Alert variant="danger">{validation}</Alert>}
        {importError && <Alert variant="danger" dismissible onClose={() => setImportError('')}>{importError}</Alert>}
        {importNotice && <Alert variant="success" dismissible onClose={() => setImportNotice('')}>{importNotice}</Alert>}

        <div className="config-admin-bulk-toolbar">
          <div className="d-flex flex-wrap gap-2">
            <Button type="button" variant="outline-primary" onClick={() => downloadSpreadsheet(false)}><i className="bi bi-file-earmark-spreadsheet me-2" aria-hidden="true"></i>Download upload template</Button>
            <Button type="button" variant="outline-primary" onClick={() => downloadSpreadsheet(true)} disabled={baselineRules.length === 0}><i className="bi bi-download me-2" aria-hidden="true"></i>Export current rules</Button>
            <Button type="button" variant="outline-primary" onClick={() => fileInputRef.current?.click()}><i className="bi bi-upload me-2" aria-hidden="true"></i>Upload spreadsheet</Button>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv,.tsv" className="d-none" onChange={handleFile} />
            <Button type="button" variant="outline-primary" onClick={() => setShowPaste((current) => !current)}><i className="bi bi-clipboard me-2" aria-hidden="true"></i>Paste table</Button>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <Button type="button" variant="outline-primary" onClick={addDependency} disabled={rows.length === 0 || dependencyGroups.length === 0}><i className="bi bi-plus-circle me-2" aria-hidden="true"></i>Add AND dependency</Button>
            <Button type="button" onClick={addRuleGroup} disabled={fieldValues.length === 0}><i className="bi bi-plus-circle me-2" aria-hidden="true"></i>Add OR rule group</Button>
          </div>
        </div>

        {showPaste && (
          <div className="config-admin-bulk-paste">
            <Form.Group>
              <Form.Label>Paste rule rows including the header row</Form.Label>
              <Form.Control as="textarea" rows={5} value={pasteText} onChange={(event) => setPasteText(event.target.value)} placeholder="Id&#9;RuleGroupId&#9;FieldValueId&#9;ParentType&#9;ParentStationId&#9;ParentFieldValueId&#9;IsDisabled&#9;IsDefaultValue" />
              <Form.Text>ParentType accepts Station or FieldValue. Matching existing IDs update staged rows; blank IDs add API rows.</Form.Text>
            </Form.Group>
            <div className="mt-2"><Button type="button" size="sm" onClick={handlePasteImport} disabled={!pasteText.trim()}>Add pasted rows</Button></div>
          </div>
        )}

        <div className="table-responsive config-admin-bulk-table-wrap">
          <Table bordered hover className="config-admin-bulk-table config-admin-rule-bulk-table mb-0">
            <thead><tr><th>ID</th><th>Rule group</th><th>Allowed value</th><th>Parent type</th><th>Parent condition</th><th>Disabled</th><th>Default</th><th>Status</th><th aria-label="Row actions"></th></tr></thead>
            <tbody>
              {rows.map((row) => {
                const rowErrors = analysis.errorsByKey[row.clientKey] || [];
                const operation = analysis.operations.find((candidate) => candidate.clientKey === row.clientKey);
                const status = rowErrors.length > 0 ? 'Error' : operation?.method === 'POST' ? 'New' : operation?.method === 'PUT' ? 'Changed' : 'Unchanged';
                return (
                  <React.Fragment key={row.clientKey}>
                    <tr className={rowErrors.length > 0 ? 'table-danger' : ''}>
                      <td><Form.Control value={row.id} disabled aria-label="Rule row ID" placeholder="New" /></td>
                      <td><Form.Control type="number" min="1" step="1" value={row.ruleGroupId} onChange={(event) => updateRow(row.clientKey, { ruleGroupId: event.target.value })} aria-label="Rule group ID" /></td>
                      <td>
                        <Form.Select value={row.fieldValueId} onChange={(event) => updateRow(row.clientKey, { fieldValueId: event.target.value })} aria-label="Allowed field value">
                          <option value="">Choose value</option>
                          {fieldValues.map((value) => <option key={value.id} value={value.id}>{getFieldValueLabel(value)} (ID {value.id})</option>)}
                        </Form.Select>
                      </td>
                      <td>
                        <Form.Select value={row.parentType} onChange={(event) => updateRow(row.clientKey, { parentType: event.target.value })} aria-label="Parent type">
                          <option value="station">Station</option>
                          <option value="fieldValue">Field value dependency</option>
                        </Form.Select>
                      </td>
                      <td>
                        {row.parentType === 'station' ? (
                          <Form.Control value={`${station.stationName} (ID ${station.id})`} disabled aria-label="Parent station" />
                        ) : (
                          <Form.Select value={row.parentFieldValueId} onChange={(event) => updateRow(row.clientKey, { parentFieldValueId: event.target.value })} aria-label="Parent field value">
                            <option value="">Choose dependency</option>
                            {dependencyGroups.map((group) => (
                              <optgroup key={group.field.id} label={`${group.field.fieldName} (Field ${group.field.id})`}>
                                {group.values.map((value) => <option key={value.id} value={value.id}>{getValueRuleOptionLabel(value, group.field)} (ID {value.id})</option>)}
                              </optgroup>
                            ))}
                          </Form.Select>
                        )}
                      </td>
                      <td className="text-center"><Form.Check type="checkbox" checked={row.isDisabled === true} onChange={(event) => updateRow(row.clientKey, { isDisabled: event.target.checked })} aria-label="Rule disabled" /></td>
                      <td className="text-center"><Form.Check type="checkbox" checked={row.isDefaultValue === true} onChange={(event) => updateRow(row.clientKey, { isDefaultValue: event.target.checked })} aria-label="Station default" /></td>
                      <td><Badge bg={status === 'Error' ? 'danger' : status === 'New' ? 'success' : status === 'Changed' ? 'warning' : 'light'} text={status === 'Unchanged' || status === 'Changed' ? 'dark' : undefined}>{status}</Badge></td>
                      <td>{!row.isExisting && <Button type="button" size="sm" variant="outline-danger" onClick={() => setRows((current) => current.filter((candidate) => candidate.clientKey !== row.clientKey))} aria-label="Remove new rule row"><i className="bi bi-trash" aria-hidden="true"></i></Button>}</td>
                    </tr>
                    {rowErrors.length > 0 && <tr className="config-admin-bulk-error-row"><td colSpan={9}>{rowErrors.join(' ')}</td></tr>}
                  </React.Fragment>
                );
              })}
              {rows.length === 0 && <tr><td colSpan={9} className="text-center text-muted py-4">No rules exist for this station and field. Add an OR rule group or upload a spreadsheet.</td></tr>}
            </tbody>
          </Table>
        </div>
      </Modal.Body>
      <Modal.Footer className="config-admin-bulk-footer">
        <div className="config-admin-bulk-summary" aria-live="polite">
          <Badge bg="success">{analysis.createCount} to create</Badge>
          <Badge bg="warning" text="dark">{analysis.updateCount} to update</Badge>
          <Badge bg="light" text="dark">{analysis.unchangedCount} unchanged</Badge>
          {hasErrors && <Badge bg="danger">{analysis.errorCount} validation {analysis.errorCount === 1 ? 'error' : 'errors'}</Badge>}
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={() => onSave({ rows, operations: analysis.operations, baselineRules })} disabled={saving || hasErrors || analysis.operations.length === 0}>
            {saving && <Spinner animation="border" size="sm" className="me-2" />}
            Save {analysis.operations.length} change{analysis.operations.length === 1 ? '' : 's'}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ValueRuleBulkEditor;
